/**
 * Simplified Coaching Handler
 *
 * Single source of truth for coaching chat flow. Uses questionCoaching.ts for
 * deterministic, question-specific responses.
 *
 * Flow:
 * 1. User answers wrong → Show explanation
 * 2. Round 1: Show reflective cards only (e.g. "I guessed", "I misread it", "I rushed")
 * 3. User clicks card → Take-home rule → Move-on (no coach response after card, no Round 2)
 * (Round 2 removed: flow is Round 1 → take-home → move-on.)
 *
 * RULE: No follow-ups may appear after the move-on message. Caller must clear
 * suggestions before showing move-on and hide suggestion UI when coaching is complete.
 */

import {
  QuestionCoaching,
  getQuestionCoaching,
  getCardsForRound
} from './questionCoaching';

export interface CoachingState {
  questionId: string;
  currentRound: 1 | 2;
  coaching: QuestionCoaching;
  isComplete: boolean;
}

/**
 * Initialize coaching for a question
 * questionIndex ensures different follow-ups per position (avoids same prompts when question IDs repeat).
 */
export function initCoaching(
  questionId: string,
  questionText: string,
  subject: string,
  correctAnswer: string,
  explanation: string,
  language: 'en' | 'nl' | 'ar' = 'en',
  questionIndex?: number
): CoachingState {
  const coaching = getQuestionCoaching(
    questionId,
    questionText,
    subject,
    correctAnswer,
    explanation,
    language,
    questionIndex
  );

  return {
    questionId,
    currentRound: 1,
    coaching,
    isComplete: false
  };
}

/**
 * Get current cards to display
 */
export function getCurrentCards(state: CoachingState): string[] {
  if (state.isComplete) return [];
  return getCardsForRound(state.coaching, state.currentRound);
}

/**
 * Handle user card selection. New flow: Round 1 only → take-home → move-on (no coach response after card, no Round 2).
 */
export function handleCardClick(
  state: CoachingState,
  _cardPrompt: string
): {
  response: string;
  nextCards: string[];
  takeHome?: string;
  moveOn?: string;
  isComplete: boolean;
  newRound: 1 | 2;
} {
  return {
    response: '',
    nextCards: [],
    takeHome: state.coaching.takeHome,
    moveOn: state.coaching.moveOn,
    isComplete: true,
    newRound: 1
  };
}

